import React from 'react';
import './App.css';
import { FontWeights, getTheme, Icon, Label, mergeStyleSets, Modal, Overlay, PrimaryButton, Spinner, SpinnerSize, DefaultButton, MessageBar, MessageBarType, Dropdown, IDropdownOption, Stack } from '@fluentui/react';
import Form from './Components/Form';
import { UserPersona } from './Components/UserPersona';
import { Car } from './Model/Car';
import { LogbookEntry } from './Model/LogbookEntry';
import { initApiService, ApiService, getApiService } from './services/ApiService';
import { useAuth } from './context/AuthContext';
import { LoginCard } from './Components/Admin/LoginCard';
import { useLogbookStore } from './store/logbookStore';
import RecentRidesPanel from './Components/RecentRidesPanel';
import CarStatePanel from './Components/CarStatePanel';
import EnvelopeInfoModal from './Components/EnvelopeInfoModal';

function App() {

  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
  const [loading, setLoading] = React.useState<boolean>(false);
  const [cars, setCars] = React.useState<Car[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [showRecentRides, setShowRecentRides] = React.useState<boolean>(false);
  const [ridesLoading, setRidesLoading] = React.useState<boolean>(false);
  const [showCarState, setShowCarState] = React.useState<boolean>(false);

  const [isEnvelopeInformationVisible, setIsEnvelopeInformationVisible] = React.useState<boolean>(false);
  const [envelopeInformation, setEnvelopeInformation] = React.useState<{ id: string, finalPrice: number } | null>(null);
  const [isCloseButtonDisabled, setIsCloseButtonDisabled] = React.useState<boolean>(false);
  const [closeCountdown, setCloseCountdown] = React.useState<number>(0);
  const [modalKey, setModalKey] = React.useState<number>(0);

  const [showExport, setShowExport] = React.useState<boolean>(false);
  const [exportCarId, setExportCarId] = React.useState<string | undefined>(undefined);
  const [exportYear, setExportYear] = React.useState<number>(new Date().getFullYear());
  const [exportLoading, setExportLoading] = React.useState<boolean>(false);
  const [exportError, setExportError] = React.useState<string | null>(null);

  const { authResponse, login, loginError, logout: authLogout, apiBase } = useAuth();
  const [apiService, setApiService] = React.useState<ApiService | null>(null);
  const { resetForm } = useLogbookStore();

  const fetchCars = React.useCallback(async () => {
    if (!apiService) return;

    try {
      setLoading(true);
      setError(null);
      const carsData = await apiService.getCars();
      setCars(carsData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error fetching cars';
      console.error('Error fetching cars:', errorMessage);
      setError(errorMessage);
      setCars([]);
    } finally {
      setLoading(false);
    }
  }, [apiService]);

  // Initialize API service when auth response is available
  React.useEffect(() => {
    if (authResponse?.token) {
      const service = initApiService(apiBase, authResponse.token, logout);
      setApiService(service);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authResponse?.token]);

  // Fetch cars when API service is ready
  React.useEffect(() => {
    if (apiService) {
      fetchCars();
    }
  }, [apiService, fetchCars]);

  // Disable close button for 5 seconds when envelope modal opens
  React.useEffect(() => {
    if (isEnvelopeInformationVisible) {
      setIsCloseButtonDisabled(true);
      setCloseCountdown(5);
      const interval = setInterval(() => {
        setCloseCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsCloseButtonDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isEnvelopeInformationVisible]);



  function logout() {
    authLogout();
    setApiService(null);
    setCars([]);
  }

  async function sendToDatabase(logbookEntry: LogbookEntry) {
    if (!apiService) {
      setError('API service not initialized');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const created = await apiService.createLogbookEntry(logbookEntry);
      setEnvelopeInformation({ id: created.envelope, finalPrice: created.finalPrice });
      setIsModalOpen(false);
      setIsEnvelopeInformationVisible(true);
      await fetchCars();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error submitting entry';
      console.error('Error submitting logbook entry:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const theme = getTheme();

  async function handleExport() {
    if (!exportCarId) {
      setExportError('Vyberte auto.');
      return;
    }
    try {
      setExportLoading(true);
      setExportError(null);
      const blob = await getApiService().exportLogbook(exportCarId, exportYear);
      const carLabel = cars.find(c => c.rowKey === exportCarId)?.licensePlate ?? exportCarId;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kniha-jizd-${carLabel.replace(/\s/g, '-')}-${exportYear}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Chyba při exportu');
    } finally {
      setExportLoading(false);
    }
  }
  const contentStyles = mergeStyleSets({
    container: {
      display: 'flex',
      flexFlow: 'column nowrap',
      alignItems: 'stretch',
    },
    mainContainer: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${theme.palette.themeLighter} 0%, ${theme.palette.white} 100%)`,
      padding: '0',
      margin: '0',
    },
    headerSection: {
      position: 'sticky' as const,
      top: 0,
      zIndex: 1000,
      background: theme.palette.white,
      boxShadow: theme.effects.elevation4,
      padding: '12px 40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '40px',
      selectors: {
        '@media (max-width: 480px)': {
          padding: '10px 16px',
        },
      },
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
    },
    logo: {
      height: '60px',
      width: 'auto',
    },
    title: {
      fontSize: '32px',
      fontWeight: FontWeights.bold,
      color: theme.palette.themeDark,
      margin: '0',
      letterSpacing: '-0.5px',
    },
    userSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '12px 20px',
      background: theme.palette.neutralLighterAlt,
      borderRadius: '8px',
      border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
    },
    userName: {
      fontWeight: FontWeights.semibold,
      color: theme.palette.neutralDark,
    },
    contentArea: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 40px',
      selectors: {
        '@media (max-width: 640px)': {
          padding: '0 12px',
        },
      },
    },
    welcomeCard: {
      background: theme.palette.white,
      borderRadius: '12px',
      padding: '40px',
      boxShadow: theme.effects.elevation8,
      textAlign: 'center',
      marginBottom: '40px',
    },
    welcomeTitle: {
      fontSize: '28px',
      fontWeight: FontWeights.bold,
      color: theme.palette.themeDark,
      marginBottom: '16px',
    },
    welcomeSubtitle: {
      fontSize: '16px',
      color: theme.palette.neutralSecondary,
      marginBottom: '32px',
      lineHeight: '1.5',
    },
    buttonContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
      justifyItems: 'stretch',
      maxWidth: '640px',
      margin: '0 auto',
      selectors: {
        '@media (max-width: 480px)': {
          gridTemplateColumns: '1fr',
        },
      },
    },
    primaryActionButton: {
      gridColumn: '1 / -1',
      width: '100%',
      height: '48px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: FontWeights.semibold,
    },
    secondaryActionButton: {
      width: '100%',
      height: '48px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: FontWeights.semibold,
      border: `2px solid ${theme.palette.themePrimary}`,
      background: theme.palette.white,
      color: theme.palette.themePrimary,
    },
    loginCard: {
      background: theme.palette.white,
      borderRadius: '12px',
      padding: '60px 40px',
      boxShadow: theme.effects.elevation8,
      textAlign: 'center',
      maxWidth: '400px',
      margin: '0 auto',
    },
    loginTitle: {
      fontSize: '24px',
      fontWeight: FontWeights.bold,
      color: theme.palette.themeDark,
      marginBottom: '16px',
    },
    loginSubtitle: {
      fontSize: '14px',
      color: theme.palette.neutralSecondary,
      marginBottom: '32px',
    },
    header: [
      {
        flex: '1 1 auto',
        borderTop: `4px solid ${theme.palette.themePrimary}`,
        color: theme.palette.neutralPrimary,
        display: 'flex',
        alignItems: 'center',
        fontWeight: FontWeights.semibold,
        padding: '12px 12px 14px 24px',
      },
    ],
    heading: {
      color: theme.palette.neutralPrimary,
      fontWeight: FontWeights.semibold,
      fontSize: 'inherit',
      margin: '0',
    },
    body: {
      flex: '4 4 auto',
      padding: '0 24px 24px 24px',
      overflowY: 'hidden',
      selectors: {
        p: { margin: '14px 0' },
        'p:first-child': { marginTop: 0 },
        'p:last-child': { marginBottom: 0 },
        '@media (max-width: 640px)': {
          padding: '0 16px 60px 16px',
        },
      },
    },
    overlay: {
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      top: '0',
      bottom: '0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'white',
      left: '0',
      verticalAlign: 'middle',
      position: 'fixed',
      right: '0',
      margin: 0,
      textAlign: 'center',
      zIndex: 1000010,
    },
    spinnerContainer: {
      background: theme.palette.white,
      borderRadius: '12px',
      padding: '40px',
      boxShadow: theme.effects.elevation16,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
    },
  });

  return (
    <div className={contentStyles.mainContainer}>
      {loading && (
        <Overlay styles={{ root: contentStyles.overlay }}>
          <div className={contentStyles.spinnerContainer}>
            <Spinner size={3} />
            <Label styles={{ root: { fontSize: '16px', fontWeight: FontWeights.semibold, color: theme.palette.neutralDark } }}>
              Načítám...
            </Label>
          </div>
        </Overlay>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} dismissButtonAriaLabel="Close" onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      <div className={contentStyles.headerSection}>
        <div className={contentStyles.logoSection}>
          <img
            className={contentStyles.logo}
            src="https://taxisever.cz/wp-content/uploads/2024/02/website_logo_transparent_background-300x146.png"
            alt="Taxi Sever"
          />
          <h1 className={contentStyles.title}>Kniha jízd</h1>
        </div>

        {authResponse !== null && (
          <UserPersona email={authResponse.email} displayName={authResponse.displayName} onLogout={logout} />
        )}
      </div>

      <div className={contentStyles.contentArea}>
        {authResponse == null && (
          <LoginCard
            onLogin={login}
            loginError={loginError}
            styles={contentStyles}
            title="Vítejte v systému Taxi Sever"
            subtitle="Pro pokračování se prosím přihlaste."
          >

          </LoginCard>
        )}

        {authResponse !== null && (
          <>
            <div className={contentStyles.welcomeCard}>
              <h2 className={contentStyles.welcomeTitle}>
                Vítejte, {authResponse.displayName ?? authResponse.email}!
              </h2>
              <p className={contentStyles.welcomeSubtitle}>
                {authResponse.isAdmin
                  ? 'Vyberte akci, kterou chcete provést. Můžete zadat novou jízdu do knihy jízd nebo přejít do administrace systému.'
                  : 'Vyberte akci, kterou chcete provést. Můžete zadat novou jízdu do knihy jízd.'}
              </p>
              <div className={contentStyles.buttonContainer}>
                <PrimaryButton
                  styles={{ root: contentStyles.primaryActionButton }}
                  onClick={() => { resetForm(); setModalKey(k => k + 1); setIsModalOpen(true); }}
                  iconProps={{ iconName: 'Add' }}
                >
                  Zadat novou jízdu
                </PrimaryButton>
                <DefaultButton
                  styles={{ root: contentStyles.secondaryActionButton }}
                  disabled={ridesLoading}
                  onClick={() => setShowRecentRides(v => !v)}
                  onRenderIcon={() => ridesLoading
                    ? <Spinner size={SpinnerSize.small} styles={{ root: { marginRight: 4 } }} />
                    : <Icon iconName={showRecentRides ? 'ChevronUp' : 'History'} styles={{ root: { marginRight: 4 } }} />
                  }
                >
                  {ridesLoading ? 'Načítám...' : showRecentRides ? 'Skrýt poslední jízdy' : 'Poslední jízdy'}
                </DefaultButton>
                <DefaultButton
                  styles={{ root: contentStyles.secondaryActionButton }}
                  onClick={() => setShowCarState(v => !v)}
                  iconProps={{ iconName: showCarState ? 'ChevronUp' : 'Car' }}
                >
                  {showCarState ? 'Skrýt stav aut' : 'Stav aut'}
                </DefaultButton>
                {authResponse.isAdmin && (
                  <DefaultButton
                    styles={{ root: contentStyles.secondaryActionButton }}
                    onClick={() => {// redirect to /admin page
                      window.location.href = '/admin';
                    }}
                    iconProps={{ iconName: 'Admin' }}
                  >
                    Administrace
                  </DefaultButton>
                )}
                <DefaultButton
                  styles={{ root: contentStyles.secondaryActionButton }}
                  onClick={() => { setShowExport(v => !v); setExportError(null); }}
                  iconProps={{ iconName: showExport ? 'ChevronUp' : 'ExcelDocument' }}
                >
                  {showExport ? 'Skrýt výpis' : 'Výpis stasky'}
                </DefaultButton>
              </div>

              {showExport && (
                <div style={{
                  marginTop: 24,
                  padding: '20px 24px',
                  background: theme.palette.neutralLighterAlt,
                  borderRadius: 8,
                  border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
                  maxWidth: 640,
                  margin: '24px auto 0',
                }}>
                  <Label styles={{ root: { fontWeight: FontWeights.semibold, fontSize: 16, marginBottom: 12 } }}>
                    📊 Export knihy jízd do Excelu
                  </Label>
                  <Stack tokens={{ childrenGap: 12 }}>
                    <Dropdown
                      label="Auto (SPZ)"
                      placeholder="Vyberte auto"
                      selectedKey={exportCarId}
                      options={cars.map((c): IDropdownOption => ({ key: c.rowKey, text: c.licensePlate + (c.carName ? ` — ${c.carName}` : '') }))}
                      onChange={(_, opt) => setExportCarId(opt?.key as string)}
                    />
                    <Dropdown
                      label="Rok"
                      selectedKey={exportYear}
                      options={Array.from({ length: 6 }, (_, i) => {
                        const y = new Date().getFullYear() - i;
                        return { key: y, text: String(y) } as IDropdownOption;
                      })}
                      onChange={(_, opt) => setExportYear(opt?.key as number)}
                    />
                    {exportError && (
                      <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setExportError(null)}>
                        {exportError}
                      </MessageBar>
                    )}
                    <PrimaryButton
                      iconProps={exportLoading ? undefined : { iconName: 'Download' }}
                      disabled={exportLoading}
                      onClick={handleExport}
                      onRenderIcon={exportLoading ? () => <Spinner size={SpinnerSize.small} styles={{ root: { marginRight: 6 } }} /> : undefined}
                    >
                      {exportLoading ? 'Generuji...' : 'Stáhnout Excel'}
                    </PrimaryButton>
                  </Stack>
                </div>
              )}

            </div>

            <RecentRidesPanel isVisible={showRecentRides} onLoadingChange={setRidesLoading} />
            <CarStatePanel isVisible={showCarState} cars={cars} />
          </>
        )}
      </div>

      {/* Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onDismiss={() => setIsModalOpen(false)}
        styles={{
          main: {
            width: "90%",
            maxWidth: "800px",
            borderRadius: "12px",
            padding: "0",
            overflow: "hidden"
          }
        }}
      >
        <div className={contentStyles.header}>
          <h2 className={contentStyles.heading}>
            ZADÁNÍ JÍZDY
          </h2>
        </div>
        <div className={contentStyles.body}>
          <Form key={modalKey} sendToDatabase={sendToDatabase} cars={cars} />
        </div>
      </Modal>

      {/* Success Modal */}
      <EnvelopeInfoModal
        isOpen={isEnvelopeInformationVisible}
        envelopeId={envelopeInformation?.id ?? null}
        finalPrice={envelopeInformation?.finalPrice ?? null}
        isCloseButtonDisabled={isCloseButtonDisabled}
        closeCountdown={closeCountdown}
        onDismiss={() => {
          setEnvelopeInformation(null);
          setIsEnvelopeInformationVisible(false);
        }}
      />

    </div>
  );
}

export default App;
